import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clone } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button, Col, Progress, Row } from "reactstrap";
import * as AccountStore from "./AccountStore";

const { Component } = React;

function TicksToDate(ticks) {
    var epoch = 621355968000000000; // ticks up to Jan 1 1970  // the date in ticks
    var date = ticks - epoch; // rebase to Jan 1 1970
    var millisec = date / 10000; // convert to milliseconds
    return new Date(millisec); // create a Javascript Date object
}

const ProfileSecurity = ({}) => {
    const [deviceFingerprint, setDeviceFingerprint] = React.useState(AccountStore.Fingerprint());
    const [credentials, setCredentials] = React.useState([]);
    const [credentialOptions, setCredentialOptions] = React.useState({ excludeCredentials: [] } as PublicKeyCredentialCreationOptions);
    const [error, setError] = React.useState("");

    const [allowCredentials, setAllowCredentials] = React.useState(false);
    const [progress, setProgress] = React.useState(0);

    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!loaded) {
            setError("");
            setLoaded(true);
            AccountStore.GetCredentials().then((creds) => {
                setCredentials(creds);
                if (navigator.credentials) {
                    AccountStore.FetchMakeCredentialOptions(deviceFingerprint).then((options) => {
                        setCredentialOptions(options);
                        setProgress(1);
                        if (options.rp.id == location.hostname) {
                            setProgress(2);
                            setAllowCredentials(true);
                        } else {
                            setError("URL is not secure");
                        }
                    });
                } else {
                    setError("This device does not support secure credentials");
                }
            });
        }
    }, [loaded]);

    const makeCredentials = async () => {
        if (credentials.filter((c) => c.deviceID == deviceFingerprint).length == 0) {
            let _options = clone(credentialOptions);

            const challengeKey = _options.user.id;
            _options.challenge = AccountStore.CoerceToArrayBuffer(_options.challenge);
            _options.user.id = AccountStore.CoerceToArrayBuffer(_options.user.id);
            if (_options.authenticatorSelection.authenticatorAttachment === null) {
                _options.authenticatorSelection.authenticatorAttachment = undefined;
            }
            _options.excludeCredentials.forEach((c) => {
                c.id = AccountStore.CoerceToArrayBuffer(c.id);
            });
            let newCredential;
            try {
                newCredential = await navigator.credentials.create({ publicKey: _options });
            } catch (e) {
                setError(e.toString());
                return;
            }
            setProgress(3);
            try {
                await registerNewCredential(newCredential, challengeKey);
            } catch (e) {
                setError(e.toString());
                return;
            }
            setProgress(4);
            AccountStore.GetCredentials().then((creds) => {
                setCredentials(creds);
            });
        } else {
            setError("This device already has credentials. Delete this device's credentials to continue");
        }
    };

    // This should be used to verify the auth data with the server
    const registerNewCredential = async (newCredential, challengeKey) => {
        let attestationObject = new Uint8Array(newCredential.response.attestationObject);
        let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
        let rawId = new Uint8Array(newCredential.rawId);

        const data = {
            id: newCredential.id,
            rawId: AccountStore.CoerceToBase64Url(rawId),
            type: newCredential.type,
            extensions: newCredential.getClientExtensionResults(),
            response: {
                attestationObject: AccountStore.CoerceToBase64Url(attestationObject),
                clientDataJSON: AccountStore.CoerceToBase64Url(clientDataJSON),
            },
        };

        let response;
        try {
            response = await AccountStore.RegisterCredentialWithServer(data, challengeKey);
        } catch (e) {
            setError(e.toString());
            return;
        }

        if (response.status !== "ok") {
            setError(response.errorMessage);
            return;
        }
    };

    const [currentUser, setCurrentUser] = React.useState(new AccountStore.Account({}));
    const [secureLoginState, setSecureLoginState] = React.useState("");

    React.useEffect(() => {
        AccountStore.GetCurrentLoggedInUser(true).then((user) => {
            setCurrentUser(user);
            let timestamp = TicksToDate(user.secureTimestamp());
            if (timestamp) {
                setSecureLoginState(+new Date(timestamp) > +new Date() - 1000 * 60 * 2 ? "verified" : "stale");
            } else {
                setSecureLoginState("unverified");
            }
        });
    }, []);

    const deleteCredential = (credentialID) => () => {
        AccountStore.DeleteCredential(credentialID).then(() => {
            setLoaded(false);
        });
    };

    const RenderSecureLoginState = () => {
        switch (secureLoginState) {
            case "verified":
                return <div className="text-success">Verified</div>;
            case "stale":
                return <div className="text-warning">Stale</div>;
            case "unverified":
                return <div className="text-muted">Unverified</div>;
            default:
                return <div className="text-muted">Unknown</div>;
        }
    };

    return (
        <div style={{ marginTop: "47px" }} className="px-4">
            <h1>
                {currentUser.firstName} {currentUser.lastName}
            </h1>
            <h2>{currentUser.email}</h2>
            <p>{currentUser.userName}</p>

            <div className="m-2">
                <Link to="/profile">Profile</Link>
                <Link to="/profile/data" className="ms-2">
                    Data
                </Link>
                <Link to="/profile/mfa" className="ms-2">
                    MFA
                </Link>
                <Link to="/profile/Security" className="ms-2">
                    Security
                </Link>
            </div>

            <Row>
                <Col md={12} lg={6}>
                    <h2>Passwordless Credentials</h2>

                    {RenderSecureLoginState()}

                    <Progress multi style={{ fontSize: "1rem", height: "1.5rem" }}>
                        <Progress bar color="warning" value={progress >= 1 ? 20 : 0}>
                            Device Secure
                        </Progress>
                        <Progress bar color="info" value={progress >= 2 ? 20 : 0}>
                            Server Secure
                        </Progress>
                        <Progress bar color="primary" value={progress >= 3 ? 20 : 0}>
                            Created Credential
                        </Progress>
                        <Progress bar color="success" value={progress >= 4 ? 40 : 0}>
                            Server Saved Credential
                        </Progress>
                        <Progress bar color="danger" value={error ? 100 - progress * 20 : 0}>
                            Error
                        </Progress>
                    </Progress>

                    <br />
                    {error && <div className="alert alert-danger">{error}</div>}
                    <br />
                    {allowCredentials && (
                        <div>
                            <Button onClick={makeCredentials} color="primary">
                                Setup Credentials
                            </Button>
                            <Button onClick={()=>{window.location.assign("/account/securelogin?returnURL=/")}} color="secondary">
                                Test Credentials
                            </Button>
                        </div>
                    )}
                    <br />
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Credential</th>
                                <th>Device</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {credentials.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.id}</td>
                                    <td>{c.deviceID}</td>
                                    <td>{c.deviceID == deviceFingerprint && <span className="text-success">This Device</span>}</td>
                                    <td>
                                        <Button outline color="danger" onClick={deleteCredential(c.id)}>
                                            <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Col>
            </Row>
        </div>
    );
};
export default ProfileSecurity;
